import axios from 'axios';
import { differenceInMinutes } from 'date-fns';
import { kebabCase } from 'lodash';
import { v4 as uuid } from 'uuid';
import config from 'config/config';
import { isUserBot } from 'src/utils/seo';
import { isAnalyticsAccepted, getBranch, getCookieValue, setFootfallCookie } from 'src/utils/cookie';
import log from 'loglevel';
import { FootfallEventType } from 'src/constants/footfallEventTypes';

const isServer = typeof window === 'undefined';
const isProduction = config.environment === 'production';
let membershipId: string | null = null;
let deviceId: string | null = null;

type Data = { [key: string]: any };

export async function trackFootfallEvent(type: FootfallEventType, data: Data = {}) {
  if (isServer || isUserBot || !isAnalyticsAccepted()) return;

  if (!membershipId || !deviceId) {
    await setSessionAndDeviceIds();
  }

  if (!isValidEvent(type, data)) {
    return log.info('Footfall data not valid', type);
  }

  const extendedData = {
    'device-id': deviceId,
    'event-type': type,
    'fe-type': 'solaris',
    'membership-id': membershipId,
    'referrer': getReferrer(),
    'session-id': getFootfallSessionId(),
    'source': getSource(),
    'url': `${window.location.pathname}${window.location.search}`.replace(/\s/g, '%20'), // replace whitespace in URL
    ...convertKeysToKebabCase(data),
  };

  if (!isProduction) {
    console.info('Footfall event:', extendedData); // eslint-disable-line no-console
  } else {
    await axios.post(
      'https://healthunlocked.com/footfall',
      JSON.stringify(extendedData),
      { headers: { 'Accept': 'application/json' } },
    ).catch(err => log.info(err, 'Error posting to Footfall'));
  }
}

// await trackFootfallEvent before changing href
export async function trackFootfallEventWithHrefDelay(href: string, type: FootfallEventType, data: Data = {}) {
  await trackFootfallEvent(type, data);
  window.location.href = href;
}

// Some events require certain keys, ideally these would be TS types!
// See for requirements: https://github.com/HealthUnlocked/footfall/blob/master/src/cljc/footfall/events.cljc
function isValidEvent(type: FootfallEventType, data: Data) {
  switch (type) {
    case 'clicked':
      return data.hasOwnProperty('clickType');
    case 'viewed-post':
      return data.hasOwnProperty('postId');
    case 'viewed-featured-post':
      return data.hasOwnProperty('featuredPostId');
    default:
      return true;
  }
}

function convertKeysToKebabCase(data: Data) {
  const formattedData: Data = {};
  Object.keys(data).forEach((key) => formattedData[kebabCase(key)] = data[key]);
  return formattedData;
}

function getSource() {
  const branch = getBranch() === 'B' ? ' B' : '';
  // Do not change to "Solaris" as it causes footfall error: Missing or unrecognised source
  // See get-schema-name function
  // https://github.com/HealthUnlocked/footfall/blob/321ee21a234a9ff7074c4115f9ef6e169f8a6e20/src/cljs/fooftall/core.cljs#L31-L35
  return `Betelgeuse${branch}`;
}

function getReferrer() { // [CHAOS-131]
  if (document.referrer.includes('healthunlocked.com')) {
    const huReferrer = getCookieValue('huReferrer');
    if (huReferrer || huReferrer === '') {
      return huReferrer;
    }
  }
  return document.referrer;
}

function getFootfallSessionId() {
  const cookie = getCookieValue('FOOTFALL_SESSION');
  if (cookie) {
    const session = JSON.parse(decodeURIComponent(cookie));
    if (session?.sessionId && differenceInMinutes(new Date(), new Date(session?.dateLastActive)) < 30) {
      setFootfallCookie({ sessionId: session.sessionId, dateLastActive: new Date() });
      return session.sessionId;
    }
  }
  // if session does not exist or is older than 30 minutes, create a new one
  return createFootfallSession();
}

function createFootfallSession() {
  const sessionId = uuid();
  setFootfallCookie({ sessionId, dateLastActive: new Date() });
  return sessionId;
}

async function setSessionAndDeviceIds() {
  const response = await axios.get(`${config.AURORA_API_PATH}session/info`).catch(err => log.info(err, 'Error getting session info'));
  if (response && response?.data?.meta?.session) {
    const sessionData = response.data.meta.session.split(':');
    if (Array.isArray(sessionData)) {
      deviceId = sessionData[0];
      membershipId = sessionData[1];
    }
  }
}

export function clearTrackingSession() {
  membershipId = null;
  deviceId = null;
}
